import { fetchRestData } from '@/services/ApiRest';
import { AGENT_API_URL } from '@/services/agent/config';
import { Badge, Box, Button, Flex, HStack, Text, VStack, useToast } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

interface ConnectedApp {
  id: string;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
}

const connectedAppsKeys = {
  list: ['agent', 'apps'] as const,
};

function ConnectedAppsSettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [connectingApp, setConnectingApp] = useState<string | null>(null);
  const [disconnectingApp, setDisconnectingApp] = useState<string | null>(null);

  const { data: apps, isLoading } = useQuery({
    queryKey: connectedAppsKeys.list,
    queryFn: async () => {
      const data = await fetchRestData<ConnectedApp[]>(`${AGENT_API_URL}/apps`);
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const handleConnect = useCallback(
    async (toolkitId: string) => {
      setConnectingApp(toolkitId);
      try {
        const result = await fetchRestData<{ auth_url: string }>(`${AGENT_API_URL}/apps/connect`, {
          method: 'POST',
          data: { toolkit_id: toolkitId },
        });
        if (result?.auth_url) {
          // Open OAuth in new window
          window.open(result.auth_url, '_blank', 'width=600,height=700');
          toast({
            title: 'Complete authorization in the new window',
            description: 'Once done, click "Refresh" to see your connection.',
            status: 'info',
            duration: 8000,
          });
        }
      } catch {
        toast({ title: 'Failed to connect', status: 'error', duration: 3000 });
      } finally {
        setConnectingApp(null);
      }
    },
    [toast],
  );

  const handleDisconnect = useCallback(
    async (toolkitId: string) => {
      setDisconnectingApp(toolkitId);
      try {
        await fetchRestData(`${AGENT_API_URL}/apps/disconnect`, {
          method: 'POST',
          data: { toolkit_id: toolkitId },
        });
        queryClient.invalidateQueries({ queryKey: connectedAppsKeys.list });
        toast({ title: 'Disconnected', status: 'info', duration: 3000 });
      } catch {
        toast({ title: 'Failed to disconnect', status: 'error', duration: 3000 });
      } finally {
        setDisconnectingApp(null);
      }
    },
    [queryClient, toast],
  );

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: connectedAppsKeys.list });
    toast({ title: 'Refreshed', status: 'success', duration: 1500 });
  }, [queryClient, toast]);

  if (isLoading) {
    return (
      <Text fontSize="sm" color="gray.500">
        Loading apps...
      </Text>
    );
  }

  return (
    <VStack alignItems="stretch" spacing={2}>
      {apps && apps.length > 0 ? (
        apps.map((app: ConnectedApp) => (
          <Flex key={app.id} justify="space-between" align="center" bg="whiteAlpha.50" borderRadius="md" px={3} py={2}>
            <HStack spacing={2} flex={1}>
              <Text fontSize="lg">{app.icon}</Text>
              <Box>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    {app.name}
                  </Text>
                  {app.connected && (
                    <Badge colorScheme="green" fontSize="2xs">
                      Connected
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="2xs" color="gray.500">
                  {app.description}
                </Text>
              </Box>
            </HStack>
            {app.connected ? (
              <Button
                size="xs"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDisconnect(app.id)}
                isLoading={disconnectingApp === app.id}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="xs"
                colorScheme="blue"
                variant="outline"
                onClick={() => handleConnect(app.id)}
                isLoading={connectingApp === app.id}
              >
                Connect
              </Button>
            )}
          </Flex>
        ))
      ) : (
        <Text fontSize="sm" color="gray.500">
          No integrations available yet.
        </Text>
      )}
      <Button size="xs" variant="ghost" colorScheme="whiteAlpha" onClick={handleRefresh}>
        Refresh connections
      </Button>
    </VStack>
  );
}

export default ConnectedAppsSettings;
